import { useState } from 'react';
import { useConstructor } from './store/useConstructor';
import { parseConstructorV2, toBackendJson } from './schema/parse';
import { getEffectiveConstructorValue } from './apply';
import { TEMPLATE_LABELS, TEMPLATE_NAMES, type ComponentTemplate } from './schema/components';

export interface ConstructorTopBarProps {
  template: ComponentTemplate;
  onTemplateChange(t: ComponentTemplate): void;
  token: string;
  onTokenChange(v: string): void;
  gameId: string;
  onGameIdChange(v: string): void;
  /** When true, don't render token/gameId input fields. */
  hideSessionInputs?: boolean;
  /** When true, don't render the template radio. The host must provide its
   *  own template control via `leadingControls`. */
  hideTemplateRadio?: boolean;
  /** Rendered after the "Design Constructor" title, before built-in
   *  template radio / session inputs. Use for selectors like Template/Pack. */
  leadingControls?: React.ReactNode;
  /** Rendered at the rightmost edge after Reset — for the primary action
   *  (e.g. Save). */
  trailingAction?: React.ReactNode;
  /**
   * @deprecated Use `leadingControls` / `trailingAction` instead. Kept for
   * back-compat — renders right before the built-in undo/redo cluster.
   */
  rightActions?: React.ReactNode;
}

// Unified topbar button style. Keep height (h-7 ≈ 28px) and padding
// identical across every control so the row reads as one strip.
const CTRL_BASE =
  'h-7 inline-flex items-center justify-center rounded border px-3 ' +
  'font-mono text-[11px] outline-none transition-colors';
const CTRL_DARK =
  CTRL_BASE + ' border-neutral-700 bg-neutral-900 text-white hover:bg-neutral-800';
const CTRL_DARK_MUTED =
  CTRL_BASE + ' border-neutral-700 bg-neutral-900 text-neutral-300 hover:bg-neutral-800';

export function ConstructorTopBar({
  template,
  onTemplateChange,
  token,
  onTokenChange,
  gameId,
  onGameIdChange,
  hideSessionInputs = false,
  hideTemplateRadio = false,
  leadingControls,
  trailingAction,
  rightActions,
}: ConstructorTopBarProps) {
  const { state, load, reset, undo, redo, canUndo, canRedo } = useConstructor();
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [copied, setCopied] = useState(false);

  const copyJson = async () => {
    try {
      await navigator.clipboard.writeText(
        JSON.stringify(toBackendJson(getEffectiveConstructorValue(state.value)), null, 2),
      );
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      // ignore if clipboard not available
    }
  };

  const applyPaste = () => {
    try {
      const parsed = parseConstructorV2(JSON.parse(pasteText));
      if (parsed) {
        load(parsed);
        setPasteOpen(false);
        setPasteText('');
      }
    } catch {
      // keep textarea open
    }
  };

  return (
    <div className="flex flex-col gap-2 border-b border-neutral-800 bg-neutral-950 px-4 py-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="font-mono text-[13px] font-semibold text-white">Design Constructor</div>

        {leadingControls}

        {!hideTemplateRadio && (
          <fieldset className="flex items-center gap-3 text-white">
            {TEMPLATE_NAMES.map((name) => (
              <label key={name} className="flex items-center gap-1 text-[12px]">
                <input
                  type="radio"
                  name="template"
                  checked={template === name}
                  onChange={() => onTemplateChange(name)}
                />
                {TEMPLATE_LABELS[name]}
              </label>
            ))}
          </fieldset>
        )}

        {!hideSessionInputs && (
          <>
            <label className="flex items-center gap-2">
              <span className="font-mono text-[10px] uppercase tracking-wide text-neutral-400">
                Token
              </span>
              <input
                type="text"
                value={token}
                onChange={(e) => onTokenChange(e.target.value)}
                className="h-7 rounded border border-neutral-700 bg-neutral-900 px-3 font-mono text-[11px] text-white outline-none"
                placeholder="dev token"
              />
            </label>
            <label className="flex items-center gap-2">
              <span className="font-mono text-[10px] uppercase tracking-wide text-neutral-400">
                Game ID
              </span>
              <input
                type="text"
                value={gameId}
                onChange={(e) => onGameIdChange(e.target.value)}
                className="h-7 w-24 rounded border border-neutral-700 bg-neutral-900 px-3 font-mono text-[11px] text-white outline-none"
                placeholder="1042"
              />
            </label>
          </>
        )}

        <div className="ml-auto flex items-center gap-2">
          {rightActions}

          <button
            type="button"
            onClick={undo}
            disabled={!canUndo}
            aria-label="Undo"
            title="Undo"
            className={CTRL_DARK + (canUndo ? '' : ' opacity-40 cursor-not-allowed')}
          >
            ⟲
          </button>
          <button
            type="button"
            onClick={redo}
            disabled={!canRedo}
            aria-label="Redo"
            title="Redo"
            className={CTRL_DARK + (canRedo ? '' : ' opacity-40 cursor-not-allowed')}
          >
            ⟳
          </button>

          <button type="button" onClick={copyJson} className={CTRL_DARK}>
            {copied ? 'Copied' : 'Copy JSON'}
          </button>
          <button
            type="button"
            onClick={() => setPasteOpen((v) => !v)}
            className={CTRL_DARK}
          >
            Paste JSON
          </button>
          <button type="button" onClick={() => reset()} className={CTRL_DARK_MUTED}>
            Reset
          </button>

          {trailingAction}
        </div>
      </div>

      {pasteOpen && (
        <div className="flex flex-col gap-2 border-t border-neutral-800 pt-2">
          <textarea
            aria-label="Paste design_system JSON"
            className="h-24 rounded border border-neutral-700 bg-neutral-900 p-2 font-mono text-[11px] text-white outline-none"
            placeholder='{ "version": 2, "palette": { "primary": "#22E11C" } }'
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
          />
          <div className="flex gap-2">
            <button type="button" onClick={applyPaste} className={CTRL_DARK}>
              Apply
            </button>
            <button
              type="button"
              onClick={() => {
                setPasteOpen(false);
                setPasteText('');
              }}
              className={CTRL_DARK_MUTED}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
